/**
 * Texts (`en`) of the «planta» slice of Quality and HACCP: what the planta App says about the quality control
 * at reception (OBR-167). Every namespace in this file is SELF-CONTAINED (`planta.qualityReception`, and the ones
 * the other slices add: self-checks, non-conformities). Own slice: `en.ts` is not touched.
 * The three languages must have the same keys (i18n.test.ts); no literal text in the views.
 */

export const plantaEn = {
  'planta.qualityReception': {
    // ── Quality control block of an entry ─────────────────────────────────────────────
    title: 'Quality control',
    temperature: 'Temperature (°C)',
    temperatureRequired: 'Temperature (°C), required',
    temperaturePlaceholder: 'For example 3.5',
    temperatureRange: 'Acceptable range: {{range}}',
    rangeBetween: '{{min}} to {{max}} °C',
    rangeAtLeast: 'At least {{min}} °C',
    rangeAtMost: 'At most {{max}} °C',
    signToggle: 'Switch the sign: positive or negative',
    status_ok: 'Within range',
    status_low: 'Below the minimum',
    status_high: 'Above the maximum',
    temperatureMissing: 'The temperature must be written down.',
    temperatureUnreadable: 'Enter a valid temperature.',
    packaging: 'Packaging',
    labeling: 'Labeling',
    checkOk: 'OK',
    checkKo: 'KO',
    issuesNotice: 'Something is off: {{list}}. A non-conformity will be recorded.',
    issue_temperature_out_of_range: 'temperature out of range',
    issue_packaging_ko: 'damaged packaging',
    issue_labeling_ko: 'incorrect labeling',
    decision: 'What do you do with it?',
    decision_accepted: 'Accept',
    decision_accepted_with_incident: 'Accept with incident',
    decision_rejected: 'Reject',
    decisionMissing: 'Choose what you do with the goods.',
    rejectedHint:
      'Rejecting the goods blocks them in stock: they cannot be used and stay pending return.',
    derivedNok: 'Non-conforming by control',

    // ── Reception screen ──────────────────────────────────────────────────────────────
    lineNeedsQuality: 'Control missing',
    qualityMissingSummary: 'The temperature or what you do with the goods is missing on an entry.',
    supplierNotApproved: 'This supplier is not approved.',
    supplierSuspended: 'This supplier is suspended.',
    supplierWarningHint: 'You can still receive the goods: the reception is recorded and will be reviewed.',

    // ── Reception detail ──────────────────────────────────────────────────────────────
    detailTemperature: 'Temperature: {{value}} °C',
    detailPackaging: 'Packaging: {{status}}',
    detailLabeling: 'Labeling: {{status}}',
    detailDecision: 'Decision: {{decision}}',
  },

  'planta.selfChecks': {
    // ── Orders home card and the Quality screen ───────────────────────────────────────
    title: 'Quality',
    cardTitle: 'Today’s self-checks',
    cardPending_one: '{{count}} self-check pending',
    cardPending_other: '{{count}} self-checks pending',
    cardAllDone: 'All the self-checks are done.',
    cardSeeAll: 'See all',
    cardOpen: 'Do the self-check: {{name}}',
    dueSection: 'Pending',
    doneSection: 'Done',
    noneDue: 'All done for now.',
    noTemplates: 'The obrador has no self-checks yet. They are set up in the BackOffice, under Quality.',
    duePeriod_daily: 'Today',
    duePeriod_weekly: 'This week',
    duePeriod_monthly: 'This month',
    kind_cleaning: 'Cleaning and disinfection',
    kind_temperature: 'Temperatures',
    kind_pests: 'Pest control',
    kind_custom: 'Other',
    frequency_daily: 'Daily',
    frequency_weekly: 'Weekly',
    frequency_monthly: 'Monthly',
    resultOk: 'OK',
    resultKo: 'KO',
    doneAt: 'Done at {{time}}',
    pendingSync: 'Waiting to be sent',
    webUnsupported: 'Self-checks are done from the mobile app.',

    // ── Filling in a self-check ───────────────────────────────────────────────────────
    instructionsTitle: 'How it is done',
    markAllOk: 'Mark the missing ones OK',
    checkOk: 'OK',
    checkKo: 'KO',
    requiredTag: 'required',
    fieldMissing: 'This field must be filled in.',
    numberPlaceholder: 'For example 3.5',
    numberUnreadable: 'Enter a valid number.',
    numberRange: 'Acceptable range: {{range}}',
    rangeBetween: '{{min}} to {{max}}{{unit}}',
    rangeAtLeast: 'At least {{min}}{{unit}}',
    rangeAtMost: 'At most {{max}}{{unit}}',
    signToggle: 'Switch the sign: positive or negative',
    status_in_range: 'Within range',
    status_out_of_range: 'Out of range',
    textPlaceholder: 'Write it here',
    photoTake: 'Take a photo',
    photoRetake: 'Retake it',
    photoRemove: 'Remove it',
    photoAttached: 'Photo attached',
    photoPendingUpload: 'It will upload when there is a connection.',
    photoPermissionDenied: 'Permission is needed to use the camera.',
    recordPhoto: 'Photo of the self-check (optional)',
    notes: 'Notes on the self-check (optional)',
    notesPlaceholder: 'Anything worth leaving in writing',
    issuesNotice: 'Something is off: {{list}}. A non-conformity will be opened.',
    issueItemKo: '{{label}} is KO',
    issueItemRange: '{{label}} out of range ({{value}}{{unit}})',
    missingSummary: 'Still to fill in: {{list}}.',
    save: 'Save',
    saving: 'Saving…',
    saveFailed: 'Could not save. Try again.',

    // ── Saved, already done, not found ────────────────────────────────────────────────
    saved: 'Self-check saved',
    savedKo: 'Saved. A non-conformity will be opened when it reaches the server.',
    savedHint: 'Without a connection it is sent by itself once you have one again.',
    nextCheck: 'Next self-check',
    nextCheckName: 'Pending: {{name}}',
    backToList: 'Back to Quality',
    alreadyDoneTitle: 'Already done',
    alreadyDoneBody: 'This self-check is already recorded as done for this period ({{result}}).',
    notFoundTitle: 'Self-check not found',
    notFoundBody: 'It may have been removed. Go back to the list.',
  },

  'planta.nonConformities': {
    reportCta: 'Report a non-conformity',
    title: 'Report a non-conformity',
    intro: 'Say what you saw. It is recorded now, also without a connection.',
    description: 'What happened?',
    descriptionPlaceholder: 'For example: broken milk crate on the dock',
    descriptionMissing: 'Say what happened.',
    relatedTitle: 'Related to (optional)',
    supplier: 'Supplier',
    article: 'Article',
    pickSupplier: 'Choose a supplier',
    pickArticle: 'Choose an article',
    change: 'Change',
    clear: 'Remove',
    search: 'Search by name or code',
    noMatch: 'No results.',
    noSuppliers: 'There are no suppliers on this device yet.',
    noArticles: 'There are no articles on this device yet.',
    close: 'Close',
    photo: 'Photo (optional)',
    photoTake: 'Take a photo',
    photoRetake: 'Retake it',
    photoRemove: 'Remove it',
    photoAttached: 'Photo attached',
    photoPendingUpload: 'It will upload when there is a connection.',
    photoPermissionDenied: 'Permission is needed to use the camera.',
    save: 'Report',
    saving: 'Sending…',
    saveFailed: 'Could not report it. Try again.',
    saved: 'Non-conformity reported',
    savedBody: 'It is recorded and will be reviewed. It gets its NC code when it reaches the server.',
    reportAnother: 'Report another',
    backToQuality: 'Back to Quality',
    historyTitle: 'Reported from this device',
    historyEmpty: 'You have not reported any yet.',
    codePending: 'Waiting to be sent',
    statusRejected: 'Could not be sent',
    withPhoto: 'With photo',
    webUnsupported: 'Non-conformities are reported from the mobile app.',
  },
} as const;
